import { useState, useEffect, useRef } from 'react';
import {
  PackageCheck,
  ScanLine,
  Loader2,
  X,
  MapPin,
  Phone,
  Camera,
  PenLine,
  CheckCircle2,
  Ban,
  ChevronLeft,
  ChevronRight,
  Truck,
  Lock,
  Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BarcodeScanner from '../components/BarcodeScanner';
import { parcelsApi, type Parcel, type ParcelStatus } from '../services/api';

const STATUS_COLORS: Record<ParcelStatus, string> = {
  CREATED: 'bg-gray-100 text-gray-700',
  ASSIGNED: 'bg-blue-100 text-blue-700',
  OUT_FOR_DELIVERY: 'bg-amber-100 text-amber-800',
  DELIVERED: 'bg-green-100 text-green-700',
  PARTIALLY_DELIVERED: 'bg-lime-100 text-lime-700',
  REFUSED: 'bg-red-100 text-red-700',
  ABSENT: 'bg-orange-100 text-orange-700',
  RETURNED: 'bg-purple-100 text-purple-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

function getPosition(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  });
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function MyParcelsPage() {
  const { t } = useTranslation();

  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date());
  const [error, setError] = useState<string | null>(null);

  // Scanner
  const [scanOpen, setScanOpen] = useState(false);
  const [scanMode, setScanMode] = useState<'lookup' | 'verify'>('lookup');

  // Delivery drawer
  const [active, setActive] = useState<Parcel | null>(null);
  const [scannedBarcode, setScannedBarcode] = useState<string | null>(null);
  const [signerName, setSignerName] = useState('');
  const [reserves, setReserves] = useState('');
  const [outcome, setOutcome] = useState<'DELIVERED' | 'PARTIALLY_DELIVERED'>('DELIVERED');
  const [proofPhotos, setProofPhotos] = useState<string[]>([]);
  const [failOutcome, setFailOutcome] = useState<'REFUSED' | 'ABSENT' | 'RETURNED'>('ABSENT');
  const [failReason, setFailReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Full-screen signature pad
  const [sigOpen, setSigOpen] = useState(false);
  const [sigUrl, setSigUrl] = useState<string | null>(null);
  const [sigPreview, setSigPreview] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = async (d: Date) => {
    setLoading(true);
    setError(null);
    try {
      setParcels(await parcelsApi.listMyToday(ymd(d)));
    } catch (e) {
      setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || t('parcels.mine.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Signature canvas setup when the full-screen pad opens (canvas only exists then).
  useEffect(() => {
    if (!sigOpen || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1f2937';
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  }, [sigOpen]);

  const scanRequired = !!active?.barcode;
  const scanOk = !scanRequired || (!!scannedBarcode && scannedBarcode === active?.barcode);

  const openDrawer = (p: Parcel, scanned?: string | null) => {
    setActive(p);
    setScannedBarcode(scanned ?? null);
    setSignerName(p.recipientName ?? '');
    setReserves('');
    setOutcome('DELIVERED');
    setProofPhotos([]);
    setFailOutcome('ABSENT');
    setFailReason('');
    setSigOpen(false);
    setSigUrl(null);
    setSigPreview(null);
  };

  const closeDrawer = () => {
    setActive(null);
    setScannedBarcode(null);
  };

  // --- Scanner handlers ---
  const onScanDetect = async (value: string) => {
    setScanOpen(false);
    if (scanMode === 'verify') {
      // Verify the scanned code matches the parcel currently open.
      if (active?.barcode && value.trim() === active.barcode.trim()) {
        setScannedBarcode(value.trim());
      } else {
        setError(t('parcels.scan.mismatch'));
      }
      return;
    }
    // Lookup mode: resolve the barcode to one of my parcels, open its drawer.
    setBusy(true);
    try {
      const p = await parcelsApi.lookup(value);
      openDrawer(p, p.barcode ?? value.trim());
    } catch {
      setError(t('parcels.scan.not_found'));
    } finally {
      setBusy(false);
    }
  };

  // --- Signature canvas ---
  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const cx = 'touches' in e ? e.touches[0]!.clientX : (e as React.MouseEvent).clientX;
    const cy = 'touches' in e ? e.touches[0]!.clientY : (e as React.MouseEvent).clientY;
    return { x: (cx - rect.left) * sx, y: (cy - rect.top) * sy };
  };
  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    drawingRef.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = getPos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const stopDraw = () => { drawingRef.current = false; };
  const clearSig = () => {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
  };
  const captureSignature = async (): Promise<string | null> => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d')!;
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let hasInk = false;
    for (let i = 0; i < img.data.length; i += 4) {
      if (img.data[i]! < 250 || img.data[i + 1]! < 250 || img.data[i + 2]! < 250) { hasInk = true; break; }
    }
    if (!hasInk) return null;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    const file = new File([blob], 'signature.png', { type: 'image/png' });
    const res = await parcelsApi.uploadProof(file);
    return res.url;
  };

  // Validate the full-screen signature pad: keep a local preview, upload, store URL.
  const confirmSignature = async () => {
    if (!canvasRef.current) { setSigOpen(false); return; }
    const dataUrl = canvasRef.current.toDataURL('image/png');
    setBusy(true);
    setError(null);
    try {
      const url = await captureSignature();
      if (url) { setSigUrl(url); setSigPreview(dataUrl); }
    } catch {
      setError(t('parcels.mine.photo_error'));
    } finally {
      setBusy(false);
      setSigOpen(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      for (const file of Array.from(files)) {
        const res = await parcelsApi.uploadProof(file);
        setProofPhotos((prev) => [...prev, res.url]);
      }
    } catch {
      setError(t('parcels.mine.photo_error'));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleStart = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const updated = await parcelsApi.markOutForDelivery(active.id);
      setActive(updated);
      await load(date);
    } catch (e) {
      setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || t('parcels.mine.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleDeliver = async () => {
    if (!active || !scanOk) return;
    setBusy(true);
    setError(null);
    try {
      const pos = await getPosition();
      const signatureUrl = sigUrl;
      await parcelsApi.deliver(active.id, {
        outcome,
        signerName: signerName || null,
        signatureUrl,
        photoUrls: proofPhotos,
        reserves: reserves || null,
        lat: pos?.lat ?? null,
        lng: pos?.lng ?? null,
        scannedBarcode: scannedBarcode ?? undefined,
      });
      closeDrawer();
      await load(date);
    } catch (e) {
      setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || t('parcels.mine.deliver_error'));
    } finally {
      setBusy(false);
    }
  };

  const handleFail = async () => {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      const pos = await getPosition();
      await parcelsApi.fail(active.id, {
        outcome: failOutcome,
        reason: failReason || null,
        lat: pos?.lat ?? null,
        lng: pos?.lng ?? null,
      });
      closeDrawer();
      await load(date);
    } catch (e) {
      setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || t('parcels.mine.error'));
    } finally {
      setBusy(false);
    }
  };

  const shiftDay = (n: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    setDate(d);
  };

  return (
    <div className="p-4 max-w-2xl mx-auto pb-24">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <PackageCheck className="w-5 h-5 text-primary-600" /> {t('parcels.mine.title')}
        </h1>
        <div className="flex items-center gap-1 text-sm">
          <button onClick={() => shiftDay(-1)} className="p-1.5 rounded hover:bg-gray-100"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-medium tabular-nums">{date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })}</span>
          <button onClick={() => shiftDay(1)} className="p-1.5 rounded hover:bg-gray-100"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : parcels.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <PackageCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>{t('parcels.mine.empty')}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {parcels.map((p) => (
            <li key={p.id}>
              <button
                onClick={() => openDrawer(p)}
                className="w-full text-left bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-gray-500">{p.reference}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status]}`}>
                    {t(`parcels.status.${p.status}`)}
                  </span>
                </div>
                <div className="font-medium text-gray-900 mt-1">{p.recipientName || '—'}</div>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {[p.recipientAddress, p.recipientPostalCode, p.recipientCity].filter(Boolean).join(', ') || '—'}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Floating scan button — lifted above the mobile bottom nav, including
          its safe-area-inset-bottom (home indicator / gesture bar) so it never
          overlaps the bar on notched devices. */}
      <button
        onClick={() => { setScanMode('lookup'); setScanOpen(true); }}
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 5rem)' }}
        className="fixed right-4 z-40 inline-flex items-center gap-2 px-5 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg font-medium"
      >
        <ScanLine className="w-5 h-5" /> {t('parcels.mine.scan')}
      </button>

      {scanOpen && (
        <BarcodeScanner onDetect={onScanDetect} onClose={() => setScanOpen(false)} />
      )}

      {/* Full-screen signature pad — safe-area aware so the header/footer clear
          the device status bar and gesture bar in the PWA. */}
      {sigOpen && (
        <div
          className="fixed inset-0 z-[70] bg-white flex flex-col"
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <span className="flex items-center gap-2 font-semibold text-gray-900">
              <PenLine className="w-5 h-5 text-indigo-600" /> {t('parcels.mine.signature')}
            </span>
            <button onClick={() => setSigOpen(false)} className="p-1 rounded hover:bg-gray-100">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 relative">
            <canvas
              ref={canvasRef}
              width={1000}
              height={700}
              className="absolute inset-0 w-full h-full touch-none"
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
            />
            <p className="absolute top-2 inset-x-0 text-center text-xs text-gray-400 pointer-events-none">
              {t('parcels.mine.sign_hint')}
            </p>
          </div>
          <div className="px-4 py-3 border-t border-gray-200 flex items-center gap-2">
            <button
              onClick={clearSig}
              className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium inline-flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" /> {t('parcels.mine.clear')}
            </button>
            <button
              onClick={confirmSignature}
              disabled={busy}
              className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg font-medium inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {t('parcels.mine.validate_sig')}
            </button>
          </div>
        </div>
      )}

      {/* Delivery drawer */}
      {active && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30" onClick={() => !busy && closeDrawer()} />
          {/* Panel offset below the status bar / above the gesture bar in the PWA. */}
          <div
            className="w-full max-w-md bg-white h-full overflow-y-auto shadow-xl p-4"
            style={{
              paddingTop: 'calc(env(safe-area-inset-top) + 1rem)',
              paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)',
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold">{active.reference}</h2>
              <button onClick={closeDrawer} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>

            {/* Tracking info */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <div className="font-medium text-gray-900">{active.recipientName || '—'}</div>
              <div className="text-gray-600 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {[active.recipientAddress, active.recipientPostalCode, active.recipientCity].filter(Boolean).join(', ') || '—'}
              </div>
              {active.recipientPhone && (
                <a href={`tel:${active.recipientPhone}`} className="text-primary-600 flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" /> {active.recipientPhone}
                </a>
              )}
              {active.orderReference && <div className="text-gray-500 text-xs">{t('parcels.f_order_ref')}: {active.orderReference}</div>}
              {active.barcode && <div className="text-gray-500 text-xs font-mono">{active.barcode}</div>}
              {active.notes && <div className="text-gray-600 text-xs mt-1">{active.notes}</div>}
            </div>

            {active.status === 'ASSIGNED' && (
              <button onClick={handleStart} disabled={busy} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-primary-300 text-primary-700 rounded-lg text-sm font-medium">
                <Truck className="w-4 h-4" /> {t('parcels.mine.start')}
              </button>
            )}

            {/* Scan gate */}
            {scanRequired && !scanOk && (
              <button
                onClick={() => { setScanMode('verify'); setScanOpen(true); }}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium"
              >
                <ScanLine className="w-5 h-5" /> {t('parcels.mine.scan_to_unlock')}
              </button>
            )}
            {scanRequired && scanOk && (
              <div className="mt-3 text-sm text-green-700 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> {t('parcels.mine.scan_ok')}
              </div>
            )}

            {/* Proof capture — disabled until scanned */}
            <fieldset disabled={!scanOk} className={`mt-3 space-y-3 ${!scanOk ? 'opacity-50' : ''}`}>
              {!scanOk && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Lock className="w-3.5 h-3.5" /> {t('parcels.mine.locked')}
                </p>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('parcels.mine.outcome')}</label>
                <select value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="DELIVERED">{t('parcels.status.DELIVERED')}</option>
                  <option value="PARTIALLY_DELIVERED">{t('parcels.status.PARTIALLY_DELIVERED')}</option>
                </select>
              </div>

              {/* Photos */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('parcels.mine.photos')}</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {proofPhotos.map((url) => (
                    <div key={url} className="relative">
                      <img src={url} alt="" className="w-16 h-16 object-cover rounded border" />
                      <button onClick={() => setProofPhotos((prev) => prev.filter((u) => u !== url))} className="absolute -top-1 -right-1 bg-white rounded-full border p-0.5">
                        <Trash2 className="w-3 h-3 text-red-600" />
                      </button>
                    </div>
                  ))}
                  <label className="w-16 h-16 flex items-center justify-center border-2 border-dashed border-gray-300 rounded cursor-pointer text-gray-400">
                    <Camera className="w-5 h-5" />
                    <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handlePhotoUpload} />
                  </label>
                </div>
              </div>

              {/* Signature — opens a full-screen pad */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <PenLine className="w-3.5 h-3.5" /> {t('parcels.mine.signature')}
                </label>
                <input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder={t('parcels.mine.signer')}
                  className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                {sigPreview ? (
                  <div className="flex items-center gap-3">
                    <img src={sigPreview} alt="" className="h-16 flex-1 min-w-0 object-contain border border-gray-200 rounded-lg bg-white" />
                    <button
                      type="button"
                      onClick={() => setSigOpen(true)}
                      className="shrink-0 px-3 py-2 text-sm border border-gray-300 rounded-lg inline-flex items-center gap-1.5"
                    >
                      <PenLine className="w-4 h-4" /> {t('parcels.mine.resign')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSigOpen(true)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium"
                  >
                    <PenLine className="w-5 h-5" /> {t('parcels.mine.sign_here')}
                  </button>
                )}
              </div>

              {/* Reserves */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('parcels.mine.reserves')}</label>
                <textarea value={reserves} onChange={(e) => setReserves(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>

              <button
                onClick={handleDeliver}
                disabled={busy || !scanOk}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg font-medium"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {t('parcels.mine.confirm')}
              </button>
            </fieldset>

            {/* Failure */}
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-gray-500 flex items-center gap-1">
                <Ban className="w-4 h-4" /> {t('parcels.mine.failure')}
              </summary>
              <div className="mt-2 space-y-2">
                <select value={failOutcome} onChange={(e) => setFailOutcome(e.target.value as typeof failOutcome)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="ABSENT">{t('parcels.status.ABSENT')}</option>
                  <option value="REFUSED">{t('parcels.status.REFUSED')}</option>
                  <option value="RETURNED">{t('parcels.status.RETURNED')}</option>
                </select>
                <input value={failReason} onChange={(e) => setFailReason(e.target.value)} placeholder={t('parcels.mine.reason')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <button onClick={handleFail} disabled={busy} className="w-full px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm font-medium">
                  {t('parcels.mine.mark_failed')}
                </button>
              </div>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}
